import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { publicAPI } from '../../api';
import { Loading } from '../../components';
import './PublicPortal.css';

ChartJS.register(ArcElement, Tooltip, Legend);

// Mapbox access token
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1IjoiZWFydGh3YXJyaW9yIiwiYSI6ImNtNGpsMW5ibjBxOHQyanM4eXdxcTB5NjgifQ.6--BvIEWTHCpRqpDLpTdBQ';

const PublicPortal = () => {
  // Date filters - default to last 1 month
  const getDefaultDates = () => {
    const today = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    return {
      from: oneMonthAgo.toISOString().split('T')[0],
      to: today.toISOString().split('T')[0]
    };
  };
  const defaultDates = getDefaultDates();
  const [fromDate, setFromDate] = useState(defaultDates.from);
  const [toDate, setToDate] = useState(defaultDates.to);
  const [searchQuery, setSearchQuery] = useState('');

  // State & City filter
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [districts, setDistricts] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [stateStats, setStateStats] = useState({ passed: 0, failed: 0 });

  // Location Type filter
  const [locationTypes, setLocationTypes] = useState([]);
  const [selectedLocationType, setSelectedLocationType] = useState('');
  const [locationTypeStats, setLocationTypeStats] = useState({ passed: 0, failed: 0 });

  // Parameter Distribution
  const [parameterStats, setParameterStats] = useState([]);

  // Failures Analysis
  const [failureThreshold, setFailureThreshold] = useState(3);
  const [failureStats, setFailureStats] = useState([]);

  // Map View
  const [mapSamples, setMapSamples] = useState([]);
  const [selectedMapSample, setSelectedMapSample] = useState(null);
  const [showSampleModal, setShowSampleModal] = useState(false);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);

  // Parameter Modal
  const [showParamModal, setShowParamModal] = useState(false);
  const [selectedParam, setSelectedParam] = useState(null);
  const [paramViewMode, setParamViewMode] = useState('list'); // 'list' or 'map'
  const [paramSamples, setParamSamples] = useState([]);
  const [paramSamplesLoading, setParamSamplesLoading] = useState(false);
  const [paramStatusFilter, setParamStatusFilter] = useState(null); // null = all, 'PASS', 'FAIL'
  const paramMapContainerRef = useRef(null);
  const paramMapRef = useRef(null);
  const paramMarkersRef = useRef([]);

  const [loading, setLoading] = useState(true);

  // Fetch initial filter options
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [statesRes, locationTypesRes] = await Promise.all([
          publicAPI.getStates(),
          publicAPI.getLocationTypes()
        ]);
        setStates(statesRes.data.data || []);

        // Transform locationTypes to always be strings
        const rawLocationTypes = locationTypesRes.data.data || [];
        const locationTypeNames = rawLocationTypes.map(type =>
          typeof type === 'string' ? type : type.name
        );
        setLocationTypes(locationTypeNames);

        // Set defaults
        if (statesRes.data.data?.length > 0) {
          setSelectedState(statesRes.data.data[0].name);
        }
        if (locationTypeNames.length > 0) {
          setSelectedLocationType(locationTypeNames[0]);
        }
      } catch (err) {
        console.error('Error fetching filters:', err);
      }
    };
    fetchFilters();
  }, []);

  // Fetch districts when state changes
  useEffect(() => {
    const fetchDistricts = async () => {
      if (!selectedState) return;
      try {
        const state = states.find(s => s.name === selectedState);
        if (state?.districts) {
          // Transform districts to strings (handle both string and object formats)
          const districtNames = state.districts.map(d =>
            typeof d === 'string' ? d : d.name
          );
          setDistricts(districtNames);
        }
      } catch (err) {
        console.error('Error fetching districts:', err);
      }
    };
    fetchDistricts();
  }, [selectedState, states]);

  // Fetch all statistics
  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;

      const [statsRes, paramStatsRes] = await Promise.all([
        publicAPI.getStats(params),
        publicAPI.getParameterStats(params)
      ]);

      // Set parameter stats
      setParameterStats(paramStatsRes.data.data || []);

      // Set failure stats
      if (statsRes.data.data?.byFailedParams) {
        setFailureStats(statsRes.data.data.byFailedParams);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  // Fetch state-based stats
  const fetchStateStats = useCallback(async () => {
    if (!selectedState) return;
    try {
      const params = { state: selectedState };
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      if (selectedDistrict) params.district = selectedDistrict;

      const response = await publicAPI.getDistrictStats(params);
      const data = response.data.data || [];

      const totalPass = data.reduce((sum, d) => sum + (d.pass || 0), 0);
      const totalFail = data.reduce((sum, d) => sum + (d.fail || 0), 0);
      setStateStats({ passed: totalPass, failed: totalFail });
    } catch (err) {
      console.error('Error fetching state stats:', err);
    }
  }, [selectedState, selectedDistrict, fromDate, toDate]);

  // Fetch location type stats
  const fetchLocationTypeStats = useCallback(async () => {
    if (!selectedLocationType) return;
    try {
      const params = { locationType: selectedLocationType };
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;

      const response = await publicAPI.getLocationTypeAnalysis(params);
      const analysis = response.data.data?.analysis || {};
      setLocationTypeStats({
        passed: analysis.acceptable || 0,
        failed: (analysis.permissible || 0) + (analysis.notAcceptable || 0)
      });
    } catch (err) {
      console.error('Error fetching location type stats:', err);
    }
  }, [selectedLocationType, fromDate, toDate]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchStateStats();
  }, [fetchStateStats]);

  useEffect(() => {
    fetchLocationTypeStats();
  }, [fetchLocationTypeStats]);

  // Fetch map samples
  const fetchMapSamples = useCallback(async () => {
    try {
      const params = {};
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;

      const response = await publicAPI.getMapData(params);
      setMapSamples(response.data.data || []);
    } catch (err) {
      console.error('Error fetching map samples:', err);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    fetchMapSamples();
  }, [fetchMapSamples]);

  // Initialize map after loading completes
  useEffect(() => {
    if (loading || !mapContainerRef.current || mapRef.current) return;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [78.9629, 20.5937], // Center of India
      zoom: 4.5
    });

    map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    mapRef.current = map;

    return () => {
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [loading]);

  // Update map markers when samples change
  useEffect(() => {
    if (!mapRef.current || loading) return;

    const addMarkers = () => {
      // Clear existing markers
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];

      // Collect bounds
      const bounds = new mapboxgl.LngLatBounds();
      let hasValidCoords = false;

      // Add new markers
      mapSamples.forEach(sample => {
        if (!sample.location?.coordinates) return;

        const [lng, lat] = sample.location.coordinates;
        if (!lng || !lat) return;

        hasValidCoords = true;
        bounds.extend([lng, lat]);

        const isPassed = sample.overallStatus === 'ACCEPTABLE';

        // Create marker element
        const el = document.createElement('div');
        el.className = 'map-marker';
        el.style.width = '24px';
        el.style.height = '24px';
        el.style.borderRadius = '50%';
        el.style.backgroundColor = isPassed ? '#10B981' : '#EF4444';
        el.style.border = '3px solid white';
        el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
        el.style.cursor = 'pointer';

        // Create popup for hover
        const popupContent = `
          <div style="padding: 8px; min-width: 200px;">
            <div style="font-weight: 600; margin-bottom: 6px; color: #1e293b;">${sample.sampleId || 'Sample'}</div>
            <div style="font-size: 12px; color: #64748b; margin-bottom: 4px;">
              <strong>Status:</strong> <span style="color: ${isPassed ? '#10B981' : '#EF4444'};">${sample.overallStatus || 'N/A'}</span>
            </div>
            <div style="font-size: 12px; color: #64748b; margin-bottom: 4px;">
              <strong>Location:</strong> ${sample.location?.district || ''}, ${sample.location?.state || ''}
            </div>
            ${sample.failedParameters?.length > 0 ? `
              <div style="font-size: 11px; color: #EF4444; margin-top: 6px;">
                <strong>Failed:</strong> ${sample.failedParameters.slice(0, 3).map(p => p.name || p).join(', ')}${sample.failedParameters.length > 3 ? '...' : ''}
              </div>
            ` : ''}
            <div style="font-size: 10px; color: #94a3b8; margin-top: 6px;">Click to view full report</div>
          </div>
        `;

        const popup = new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: false,
          offset: 15
        }).setHTML(popupContent);

        // Create marker
        const marker = new mapboxgl.Marker(el)
          .setLngLat([lng, lat])
          .addTo(mapRef.current);

        // Hover events
        el.addEventListener('mouseenter', () => {
          popup.setLngLat([lng, lat]).addTo(mapRef.current);
        });
        el.addEventListener('mouseleave', () => {
          popup.remove();
        });

        // Click event
        el.addEventListener('click', () => {
          setSelectedMapSample(sample);
          setShowSampleModal(true);
        });

        markersRef.current.push(marker);
      });

      // Fit map to bounds if we have valid coordinates
      if (hasValidCoords) {
        mapRef.current.fitBounds(bounds, {
          padding: 50,
          maxZoom: 12
        });
      }
    };

    // Wait for map to be loaded before adding markers
    if (mapRef.current.loaded()) {
      addMarkers();
    } else {
      mapRef.current.on('load', addMarkers);
    }
  }, [mapSamples, loading]);

  // Fetch samples for parameter with optional status filter
  const fetchParamSamples = useCallback(async (param, statusFilter = null) => {
    setParamSamplesLoading(true);
    try {
      const params = { parameterCode: param.code, limit: 100 };
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      // Convert PASS/FAIL to actual status
      if (statusFilter === 'PASS') params.status = 'ACCEPTABLE';
      else if (statusFilter === 'FAIL') params.status = 'NOT_ACCEPTABLE';

      const response = await publicAPI.getSamplesByParameter(params);
      setParamSamples(response.data.data?.samples || []);
    } catch (err) {
      console.error('Error fetching parameter samples:', err);
      setParamSamples([]);
    } finally {
      setParamSamplesLoading(false);
    }
  }, [fromDate, toDate]);

  // Handle parameter click - fetch samples and show modal
  const handleParameterClick = async (param) => {
    setSelectedParam(param);
    setShowParamModal(true);
    setParamViewMode('list');
    setParamStatusFilter(null);
    fetchParamSamples(param, null);
  };

  // Handle status filter click
  const handleStatusFilter = (status) => {
    const newStatus = paramStatusFilter === status ? null : status;
    setParamStatusFilter(newStatus);
    if (selectedParam) {
      fetchParamSamples(selectedParam, newStatus);
    }
  };

  // Initialize parameter modal map when switching to map view
  useEffect(() => {
    if (!showParamModal || paramViewMode !== 'map' || !paramMapContainerRef.current) return;

    // Clean up existing map
    if (paramMapRef.current) {
      paramMarkersRef.current.forEach(marker => marker.remove());
      paramMarkersRef.current = [];
      paramMapRef.current.remove();
      paramMapRef.current = null;
    }

    // Create new map
    const map = new mapboxgl.Map({
      container: paramMapContainerRef.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [78.9629, 20.5937],
      zoom: 4
    });

    map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    paramMapRef.current = map;

    map.on('load', () => {
      const bounds = new mapboxgl.LngLatBounds();
      let hasValidCoords = false;

      paramSamples.forEach(sample => {
        if (!sample.location?.coordinates) return;
        const [lng, lat] = sample.location.coordinates;
        if (!lng || !lat) return;

        hasValidCoords = true;
        bounds.extend([lng, lat]);

        const isPassed = sample.parameter?.status === 'ACCEPTABLE';

        const el = document.createElement('div');
        el.style.width = '20px';
        el.style.height = '20px';
        el.style.borderRadius = '50%';
        el.style.backgroundColor = isPassed ? '#10B981' : '#EF4444';
        el.style.border = '2px solid white';
        el.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';
        el.style.cursor = 'pointer';

        const popup = new mapboxgl.Popup({ offset: 15 }).setHTML(`
          <div style="padding: 8px;">
            <div style="font-weight: 600; color: #1e293b;">${sample.sampleId}</div>
            <div style="font-size: 12px; color: #64748b;">${sample.parameter?.name}: ${sample.parameter?.value} ${sample.parameter?.unit || ''}</div>
            <div style="font-size: 11px; color: ${isPassed ? '#10B981' : '#EF4444'};">${sample.parameter?.status}</div>
          </div>
        `);

        const marker = new mapboxgl.Marker(el)
          .setLngLat([lng, lat])
          .setPopup(popup)
          .addTo(map);

        paramMarkersRef.current.push(marker);
      });

      if (hasValidCoords) {
        map.fitBounds(bounds, { padding: 40, maxZoom: 10 });
      }
    });

    return () => {
      paramMarkersRef.current.forEach(marker => marker.remove());
      paramMarkersRef.current = [];
      if (paramMapRef.current) {
        paramMapRef.current.remove();
        paramMapRef.current = null;
      }
    };
  }, [showParamModal, paramViewMode, paramSamples]);

  // Calculate failure count based on threshold
  const getFailureCount = () => {
    return failureStats
      .filter(f => f.failedCount >= failureThreshold)
      .reduce((sum, f) => sum + f.samples, 0);
  };

  // Semi-circle gauge component - shows pass rate
  const SemiCircleGauge = ({ passed, failed }) => {
    const total = passed + failed;
    // If no samples, show 50/50 split
    const passPercentage = total === 0 ? 50 : Math.round((passed / total) * 100);
    // Calculate pass angle: 100% = 180deg (full green), 0% = 0deg (full red)
    const passAngle = (passPercentage / 100) * 180;

    return (
      <div className="gauge-container">
        <div className="gauge-stats">
          <div className="gauge-stat passed">
            <span className="stat-value">{passed}</span>
            <span className="stat-label">PASSED</span>
          </div>
          <div className="gauge-visual">
            <div className="semi-circle">
              <div
                className="semi-circle-bg"
                style={{
                  background: `conic-gradient(#3DD9D6 0deg ${passAngle}deg, #F87171 ${passAngle}deg 180deg, transparent 180deg 360deg)`,
                  transform: 'rotate(-90deg)'
                }}
              ></div>
              <div className="semi-circle-cover"></div>
              <div className="gauge-percentage">{total === 0 ? '-' : `${passPercentage}%`}</div>
            </div>
          </div>
          <div className="gauge-stat failed">
            <span className="stat-value">{failed}</span>
            <span className="stat-label">FAILED</span>
          </div>
        </div>
      </div>
    );
  };

  // Parameter distribution chart data
  const parameterChartData = {
    labels: parameterStats.slice(0, 5).map(p => p.name),
    datasets: [{
      data: parameterStats.slice(0, 5).map(p => p.acceptable + p.permissible + p.notAcceptable),
      backgroundColor: [
        '#3DD9D6',
        '#6B7FE3',
        '#F87171',
        '#E0E7FF',
        '#FBBF24'
      ],
      borderWidth: 0
    }]
  };

  const parameterChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    },
    cutout: '60%',
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        const param = parameterStats[index];
        if (param) {
          handleParameterClick(param);
        }
      }
    }
  };

  // Handle reset filters
  const handleReset = () => {
    const defaults = getDefaultDates();
    setFromDate(defaults.from);
    setToDate(defaults.to);
    setSearchQuery('');
  };

  // Calculate total samples
  const totalSamples = mapSamples.length;

  if (loading) {
    return <Loading message="Loading analytics..." />;
  }

  return (
    <div className="analytics-dashboard">
      <div className="analytics-header">
        <h1>Sample Analytics Full Dashboard</h1>
      </div>

      {/* Filter Bar */}
      <div className="filter-card">
        <div className="filter-bar">
          <div className="total-samples-badge">
            <span className="total-count">{totalSamples}</span>
            <span className="total-label">Total Samples</span>
          </div>
          <div className="search-input-wrapper">
            <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input
              type="text"
              className="search-input"
              placeholder="Search by name, address, sample ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="date-filters">
            <span className="date-label">From:</span>
            <input
              type="date"
              className="date-input"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
            <span className="date-label">To:</span>
            <input
              type="date"
              className="date-input"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          <div className="filter-buttons">
            <button className="btn-primary" onClick={() => fetchStats()}>Search</button>
            <button className="btn-outline" onClick={handleReset}>Reset</button>
          </div>
        </div>
      </div>

      {/* State & Location Type Cards */}
      <div className="analytics-grid two-cols">
        {/* State & City Based Samples */}
        <div className="analytics-card">
          <h3>State & City Based Samples</h3>
          <div className="filter-dropdowns">
            <select
              value={selectedState}
              onChange={(e) => {
                setSelectedState(e.target.value);
                setSelectedDistrict('');
              }}
            >
              {states.map(state => (
                <option key={state._id} value={state.name}>{state.name}</option>
              ))}
            </select>
            <select
              value={selectedDistrict}
              onChange={(e) => setSelectedDistrict(e.target.value)}
            >
              <option value="">Select City</option>
              {districts.map((district, idx) => (
                <option key={idx} value={district}>{district}</option>
              ))}
            </select>
          </div>
          <p className="card-subtitle">
            Samples — {selectedState} {selectedDistrict ? `(${selectedDistrict})` : '(All Cities)'}
          </p>
          <SemiCircleGauge passed={stateStats.passed} failed={stateStats.failed} />
        </div>

        {/* Location Type Based Samples */}
        <div className="analytics-card">
          <h3>Location Type Based Samples</h3>
          <div className="filter-dropdowns single">
            <select
              value={selectedLocationType}
              onChange={(e) => setSelectedLocationType(e.target.value)}
            >
              {locationTypes.map((type, idx) => (
                <option key={idx} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <p className="card-subtitle">Samples — {selectedLocationType}</p>
          <SemiCircleGauge passed={locationTypeStats.passed} failed={locationTypeStats.failed} />
        </div>
      </div>

      {/* Parameter Distribution & Failures Analysis */}
      <div className="analytics-grid two-cols">
        {/* Samples by Parameter Distribution */}
        <div className="analytics-card">
          <h3>Samples by Parameter Distribution</h3>
          <div className="parameter-section-centered">
            <div className="parameter-chart-container">
              <div className="donut-chart">
                <Doughnut data={parameterChartData} options={parameterChartOptions} />
              </div>
              <div className="parameter-legend">
                <p className="legend-title">KEY PARAMETERS</p>
                {parameterStats.slice(0, 5).map((param, idx) => (
                  <div
                    key={idx}
                    className="legend-item clickable"
                    onClick={() => handleParameterClick(param)}
                  >
                    <span
                      className="legend-color"
                      style={{ backgroundColor: parameterChartData.datasets[0].backgroundColor[idx] }}
                    ></span>
                    <span className="legend-label">{param.name}</span>
                  </div>
                ))}
              </div>
            </div>
            <p className="chart-description">Breakdown of tested water quality indicators</p>
          </div>
        </div>

        {/* Failures Analysis */}
        <div className="analytics-card">
          <h3>Failures Analysis</h3>
          <p className="card-hint">Select the minimum number of parameters failed to see total sample impact.</p>
          <div className="failure-tabs">
            {[1, 2, 3, 4, 5].map(num => (
              <button
                key={num}
                className={`failure-tab ${failureThreshold === num ? 'active' : ''}`}
                onClick={() => setFailureThreshold(num)}
              >
                {num}+
              </button>
            ))}
          </div>
          <div className="failure-result">
            <span className="failure-count">{getFailureCount()}</span>
            <span className="failure-label">TOTAL SAMPLES</span>
            <span className="failure-desc">
              failing on <span className="highlight">{failureThreshold} or more</span> parameters
            </span>
          </div>
        </div>
      </div>

      {/* Section 5: Map View */}
      <div className="analytics-card map-view-card">
        <h3>Map View - Sample Locations</h3>
        <p className="card-hint">
          View all sample locations on the map.
          <span className="legend-inline">
            <span className="marker-legend passed"></span> Passed
            <span className="marker-legend failed"></span> Failed
          </span>
          Hover to see details, click for full report.
        </p>
        <div className="map-container" ref={mapContainerRef}></div>
      </div>

      {/* Sample Report Modal */}
      {showSampleModal && selectedMapSample && (
        <div className="modal-overlay" onClick={() => setShowSampleModal(false)}>
          <div className="modal-content sample-report-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Sample Report</h2>
              <button className="modal-close" onClick={() => setShowSampleModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="report-header">
                <div className="report-id">
                  <span className="label">Sample ID</span>
                  <span className="value">{selectedMapSample.sampleId}</span>
                </div>
                <div className={`report-status ${selectedMapSample.overallStatus === 'ACCEPTABLE' ? 'passed' : 'failed'}`}>
                  {selectedMapSample.overallStatus || 'N/A'}
                </div>
              </div>

              <div className="report-section">
                <h4>Sample Details</h4>
                <div className="report-grid">
                  {selectedMapSample.address && (
                    <div className="report-field">
                      <span className="label">Address</span>
                      <span className="value">{selectedMapSample.address}</span>
                    </div>
                  )}
                  {selectedMapSample.collectedAt && (
                    <div className="report-field">
                      <span className="label">Collected On</span>
                      <span className="value">{new Date(selectedMapSample.collectedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="report-section">
                <h4>Location Details</h4>
                <div className="report-grid">
                  <div className="report-field">
                    <span className="label">State</span>
                    <span className="value">{selectedMapSample.location?.state || 'N/A'}</span>
                  </div>
                  <div className="report-field">
                    <span className="label">District</span>
                    <span className="value">{selectedMapSample.location?.district || 'N/A'}</span>
                  </div>
                  <div className="report-field">
                    <span className="label">Location Type</span>
                    <span className="value">{selectedMapSample.locationType || 'N/A'}</span>
                  </div>
                  <div className="report-field">
                    <span className="label">Water Source</span>
                    <span className="value">{selectedMapSample.waterSource || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {selectedMapSample.parameters?.length > 0 && (
                <div className="report-section">
                  <h4>Test Results</h4>
                  <div className="table-container">
                    <table className="table" style={{ minWidth: '500px' }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'center', width: '50px' }}>S.N.</th>
                          <th>Parameter</th>
                          <th style={{ textAlign: 'center' }}>Value</th>
                          <th style={{ textAlign: 'center' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedMapSample.parameters.map((param, idx) => (
                          <tr key={idx} style={{
                            backgroundColor: param.status === 'ACCEPTABLE' ? '#dcfce7' :
                              param.status === 'PERMISSIBLE' ? '#fef3c7' :
                              param.status === 'NOT_ACCEPTABLE' ? '#fee2e2' : 'transparent'
                          }}>
                            <td style={{ textAlign: 'center', fontWeight: 600, color: '#6b7280' }}>{idx + 1}</td>
                            <td style={{ fontWeight: 500 }}>{param.name}</td>
                            <td style={{ textAlign: 'center' }}>{param.value}{param.unit ? ` ${param.unit}` : ''}</td>
                            <td style={{ textAlign: 'center' }}>
                              <span className={`badge badge-${(param.status || 'n/a').toLowerCase()}`}>
                                {param.status === 'ACCEPTABLE' ? 'PASS' : param.status === 'NOT_ACCEPTABLE' ? 'FAIL' : param.status === 'PERMISSIBLE' ? 'PERMISSIBLE' : 'N/A'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {(selectedMapSample.images?.sampleImageUrl || selectedMapSample.images?.locationImageUrl) && (
                <div className="report-section">
                  <h4>Images</h4>
                  <div className="report-grid">
                    {selectedMapSample.images.sampleImageUrl && (
                      <div className="report-field">
                        <span className="label">Sample Image</span>
                        <img src={selectedMapSample.images.sampleImageUrl} alt="Sample" style={{ width: '100%', borderRadius: '8px', marginTop: '8px', maxHeight: '200px', objectFit: 'cover' }} />
                      </div>
                    )}
                    {selectedMapSample.images.locationImageUrl && (
                      <div className="report-field">
                        <span className="label">Location Image</span>
                        <img src={selectedMapSample.images.locationImageUrl} alt="Location" style={{ width: '100%', borderRadius: '8px', marginTop: '8px', maxHeight: '200px', objectFit: 'cover' }} />
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="report-actions">
                <button
                  className="btn-download"
                  onClick={() => publicAPI.downloadPDF(selectedMapSample._id, selectedMapSample.sampleId)}
                >
                  Download PDF Report
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Parameter Samples Modal */}
      {showParamModal && selectedParam && (
        <div className="modal-overlay" onClick={() => setShowParamModal(false)}>
          <div className="modal-content param-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedParam.name} - Samples</h2>
              <button className="modal-close" onClick={() => setShowParamModal(false)}>&times;</button>
            </div>
            <div className="modal-body param-modal-body">
              {/* Left Side - Filters */}
              <div className="param-filters-sidebar">
                <div className="view-toggle-vertical">
                  <button
                    className={`toggle-btn ${paramViewMode === 'list' ? 'active' : ''}`}
                    onClick={() => setParamViewMode('list')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="8" y1="6" x2="21" y2="6"></line>
                      <line x1="8" y1="12" x2="21" y2="12"></line>
                      <line x1="8" y1="18" x2="21" y2="18"></line>
                      <line x1="3" y1="6" x2="3.01" y2="6"></line>
                      <line x1="3" y1="12" x2="3.01" y2="12"></line>
                      <line x1="3" y1="18" x2="3.01" y2="18"></line>
                    </svg>
                    List View
                  </button>
                  <button
                    className={`toggle-btn ${paramViewMode === 'map' ? 'active' : ''}`}
                    onClick={() => setParamViewMode('map')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon>
                      <line x1="8" y1="2" x2="8" y2="18"></line>
                      <line x1="16" y1="6" x2="16" y2="22"></line>
                    </svg>
                    Map View
                  </button>
                </div>

                <div className="filter-divider"></div>

                {/* Status Filters - Vertical */}
                <div className="param-status-filters">
                  <p className="filter-title">Filter by Status</p>
                  <div
                    className={`status-filter-item all ${paramStatusFilter === null ? 'active' : ''}`}
                    onClick={() => handleStatusFilter(null)}
                  >
                    <span className="count">{selectedParam.total || (selectedParam.acceptable + selectedParam.permissible + selectedParam.notAcceptable)}</span>
                    <span className="label">All</span>
                  </div>
                  <div
                    className={`status-filter-item pass ${paramStatusFilter === 'PASS' ? 'active' : ''}`}
                    onClick={() => handleStatusFilter('PASS')}
                  >
                    <span className="count">{selectedParam.acceptable || 0}</span>
                    <span className="label">Pass</span>
                  </div>
                  <div
                    className={`status-filter-item fail ${paramStatusFilter === 'FAIL' ? 'active' : ''}`}
                    onClick={() => handleStatusFilter('FAIL')}
                  >
                    <span className="count">{(selectedParam.permissible || 0) + (selectedParam.notAcceptable || 0)}</span>
                    <span className="label">Fail</span>
                  </div>
                </div>
              </div>

              {/* Right Side - Content */}
              <div className="param-content-area">
                {paramSamplesLoading ? (
                  <div className="param-loading">Loading samples...</div>
                ) : paramViewMode === 'list' ? (
                  <div className="param-samples-list">
                    {paramSamples.length === 0 ? (
                      <div className="no-samples">No samples found for this parameter</div>
                    ) : (
                      paramSamples.map((sample, idx) => {
                        const isPass = sample.parameter?.status === 'ACCEPTABLE';
                        return (
                          <div key={idx} className={`param-sample-item ${isPass ? 'pass' : 'fail'}`}>
                            <div className="sample-main">
                              <span className="sample-id">{sample.sampleId}</span>
                              <span className={`sample-status ${isPass ? 'pass' : 'fail'}`}>
                                {isPass ? 'PASS' : 'FAIL'}
                              </span>
                            </div>
                            <div className="sample-details">
                              <span className="sample-value">
                                Value: <strong>{sample.parameter?.value}</strong> {sample.parameter?.unit || ''}
                              </span>
                              <span className="sample-location">
                                {sample.location?.district}, {sample.location?.state}
                              </span>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                ) : (
                  <div className="param-map-container" ref={paramMapContainerRef}></div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicPortal;
